import React, { useState, useEffect } from "react";
import styled from "styled-components";
import DefaultLogo from "../../../../assets/logo.svg";
import { Button } from "../../../general/Button";
import { Typography } from "../../../general/Typography";
import { Menu, X, User, LogOut, Sun, Moon, PlusCircle } from "lucide-react";
import { Flex } from "../../wrappers/Flex";

interface User {
  name: string;
  avatar?: string;
}

interface HeaderProps {
  user?: User;
  onLogin?: () => void;
  onLogout?: () => void;
  onSignUp?: () => void;
  onThemeToggle?: () => void;
  isDarkMode?: boolean;
  showFAB?: boolean;
  showTitleOnMobile?: boolean;
  shadowStrength?: "none" | "light" | "medium" | "strong";
  logo?: string;
  title?: string;
  actions?: React.ReactNode;
}

const getShadow = (strength: string) => {
  switch (strength) {
    case "light": return "0px 2px 6px rgba(0,0,0,0.05)";
    case "medium": return "0px 4px 12px rgba(0,0,0,0.08)";
    case "strong": return "0px 6px 18px rgba(0,0,0,0.15)";
    default: return "none";
  }
};

const StyledHeader = styled.header<{ shadowStrength: string; isScrolled: boolean }>`
  position: sticky;
  top: 0;
  z-index: 1000;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.textPrimary};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: ${({ shadowStrength, isScrolled }) => isScrolled ? getShadow(shadowStrength) : "none"};
  transition: box-shadow 0.2s ease-in-out;

  .logo {
    height: 42px;
    max-width: 140px;
    object-fit: contain;
  }

  .menu-button {
    display: none;
    background: none;
    border: none;
    cursor: pointer;
    color: inherit;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
    .menu-button {
      display: block;
    }
  }
`;

const Avatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
`;

const MobileMenu = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: ${({ isOpen }) => (isOpen ? "0" : "-100%")};
  width: 80%;
  height: 100%;
  background: ${({ theme }) => theme.colors.surface};
  box-shadow: ${({ theme }) => theme.elevations.md};
  transition: left 0.3s ease-in-out;
  padding: ${({ theme }) => theme.spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  z-index: 999;

  .close-button {
    align-self: flex-end;
    background: none;
    border: none;
    cursor: pointer;
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const FloatingButton = styled.button`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.buttonText};
  border: none;
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.radii.rounded};
  box-shadow: ${({ theme }) => theme.elevations.high};
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.08);
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

export const Header: React.FC<HeaderProps> = ({
  user,
  onLogin,
  onLogout,
  onSignUp,
  onThemeToggle,
  isDarkMode = false,
  showFAB = true,
  showTitleOnMobile = false,
  shadowStrength = "light",
  logo,
  title = "USHI",
  actions,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <StyledHeader shadowStrength={shadowStrength} isScrolled={isScrolled}>
        <Flex align="center" gap="md">
          <img src={logo || DefaultLogo} alt="App Logo" className="logo" />
          {showTitleOnMobile && (
            <Typography as="h1" variant="title" weight="bold">
              {title}
            </Typography>
          )}
        </Flex>

        <Flex align="center" gap="md" className="desktop-actions" style={{ display: "none", flexWrap: "wrap" }}>
          {/* Optional actions passed in */}
          {actions}

          {onThemeToggle && (
            <Button size="small" variant="text" onClick={onThemeToggle} aria-label="Toggle theme">
              {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
            </Button>
          )}

          {user ? (
            <Flex align="center" gap="sm">
              {user.avatar ? (
                <Avatar src={user.avatar} alt={user.name} />
              ) : (
                <User size={24} />
              )}
              <Typography variant="body">{user.name}</Typography>
              <Button size="small" variant="secondary" onClick={onLogout}>
                <LogOut size={16} /> Log out
              </Button>
            </Flex>
          ) : (
            <Flex gap="sm">
              <Button size="small" variant="primary" onClick={onLogin}>Log in</Button>
              <Button size="small" variant="secondary" onClick={onSignUp}>Sign up</Button>
            </Flex>
          )}
        </Flex>

        <button className="menu-button" onClick={() => setMenuOpen(true)}>
          <Menu size={24} />
        </button>
      </StyledHeader>

      {/* Mobile Menu */}
      <MobileMenu isOpen={menuOpen}>
        <button className="close-button" onClick={() => setMenuOpen(false)}>
          <X size={28} />
        </button>

        {user ? (
          <>
            <Typography variant="body" weight="bold">{user.name}</Typography>
            <Button fullWidth variant="secondary" onClick={onLogout}>Log out</Button>
          </>
        ) : (
          <>
            <Button fullWidth variant="primary" onClick={onLogin}>Log in</Button>
            <Button fullWidth variant="secondary" onClick={onSignUp}>Sign up</Button>
          </>
        )}

        {onThemeToggle && (
          <Button fullWidth variant="text" onClick={onThemeToggle}>
            {isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
          </Button>
        )}
      </MobileMenu>

      {showFAB && (
        <FloatingButton aria-label="Quick action">
          <PlusCircle size={24} />
        </FloatingButton>
      )}
    </>
  );
};